
import { useState, useEffect, useCallback, useMemo } from "react";
import { DiaryEntry } from "@/utils/diary/types";
import { getUserEntries } from "@/utils/diary/storage";
import { subMonths } from "date-fns";
import { toast } from "sonner";

// Increased cache expiration to 10 minutes to improve performance
const CACHE_EXPIRATION = 10 * 60 * 1000;

export function useEntriesData(userId: string | undefined) {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [timeRange, setTimeRange] = useState<string>("3months");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<number>(Date.now());

  // Load entries when userId changes - with useCallback to avoid unnecessary rerenders
  const loadEntries = useCallback(async () => {
    if (!userId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First, try to get data from sessionStorage for immediate display
      const cacheKey = `diary_entries_${userId}`;
      let userEntries: DiaryEntry[] = [];
      let shouldFetchFresh = true;
      
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          
          // Check if cache is still valid (within expiration window)
          if (Date.now() - timestamp < CACHE_EXPIRATION) {
            userEntries = data;
            shouldFetchFresh = false;
            
            // Set entries from cache immediately
            if (Array.isArray(userEntries) && userEntries.length > 0) {
              userEntries.sort((a, b) => {
                return new Date(b.date || b.createdAt).getTime() - 
                      new Date(a.date || a.createdAt).getTime();
              });
              setEntries(userEntries);
              setIsLoading(false);
            }
          }
        }
      } catch (cacheError) {
        console.warn('Error reading from cache:', cacheError);
        // Continue with fresh fetch
      }
      
      // Fetch fresh data if needed
      if (shouldFetchFresh) {
        console.log(`Loading entries for user: ${userId}`);
        const startTime = performance.now();
        
        userEntries = await getUserEntries(userId);
        
        const endTime = performance.now();
        console.log(`Entries loaded in ${Math.round(endTime - startTime)}ms, count: ${userEntries.length}`);
        
        // Only attempt to cache if we have entries
        if (Array.isArray(userEntries) && userEntries.length > 0) {
          try {
            const dataToCache = { 
              data: userEntries, 
              timestamp: Date.now() 
            };
            
            // Try to store in sessionStorage, but don't fail if it exceeds quota
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
            } catch (storageError) {
              // Just log but don't fail if storage quota is exceeded
              console.warn('Unable to cache entries in sessionStorage:', storageError);
            }
          } catch (cacheError) {
            console.warn('Error caching entries data:', cacheError);
          }
        }
      }
      
      if (Array.isArray(userEntries)) {
        // Sort entries by date
        userEntries.sort((a, b) => {
          return new Date(b.date || b.createdAt).getTime() - 
                new Date(a.date || a.createdAt).getTime();
        });
        
        setEntries(userEntries);
      } else {
        console.error("Expected array from getUserEntries but got:", userEntries);
        setEntries([]);
        setError("Invalid data format received");
      }
    } catch (error) {
      console.error("Error loading entries:", error);
      setError("Failed to load your diary entries");
      setEntries([]);
      toast.error("Failed to load entries. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries, lastRefreshed]);

  const refreshEntries = useCallback(() => {
    // Clear cache and force refresh
    if (userId) {
      try {
        const cacheKey = `diary_entries_${userId}`;
        sessionStorage.removeItem(cacheKey);
      } catch (error) {
        console.warn('Error clearing cache:', error);
      }
    }
    setLastRefreshed(Date.now());
  }, [userId]);

  // Memoize filtered entries to prevent recalculation on every render
  const getFilteredEntries = useCallback(() => {
    const now = new Date();
    
    switch (timeRange) {
      case "1month":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 1);
        });
      case "3months":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 3);
        });
      case "6months":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 6);
        });
      case "1year":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 12);
        });
      case "all":
        return entries;
      default:
        return entries;
    }
  }, [entries, timeRange]);

  return {
    entries,
    timeRange, 
    setTimeRange,
    getFilteredEntries,
    isLoading,
    error,
    refreshEntries
  };
}
